/**
 * Thai -> Latin transliteration for road names.
 *
 * Mapbox Directions returns OSM road names verbatim and ignores `language`
 * for them: asking for English on a Bangkok route still yields
 * "ถนนราชดำริ" with no `ref` to fall back on (verified against the live API,
 * 2026-08-15 - central Bangkok streets come back Thai-only). That left the
 * sign with three bad options: show Thai the driver cannot read, show the
 * road CLASS ("Main road" - not a name, and the driver rejected it), or show
 * nothing.
 *
 * So we romanise. "ถนนราชดำริ" -> "Thanon Ratchadamri" is a real name the
 * driver can match against the romanised label on the map and, often, the
 * English half of the street sign on the corner.
 *
 * This follows the Royal Thai General System of Transcription (RTGS) closely
 * enough to be recognisable, which is the goal - it is a legibility aid on a
 * road sign, not a linguistic tool. Known and accepted departures:
 *
 *  - RTGS drops tone and length distinctions; so do we. "ไม้" and "ใหม่" both
 *    romanise to "mai", exactly as they do on real Thai road signs.
 *  - Vowel placement in Thai is non-linear (a vowel glyph can sit before the
 *    consonant it follows). We handle the common leading vowels explicitly;
 *    rarer stacked forms come out approximately.
 *  - No dictionary of exceptions. "ถนน" -> "Thanon" is handled as a word
 *    because it prefixes a large share of road names, but we do not attempt
 *    to map every place name to its conventional English spelling.
 *
 * Where a conventional spelling matters more than a mechanical one, WORDS
 * below holds it.
 */

/**
 * Road and place names with an established English spelling.
 *
 * These are checked before the letter rules, because a character-level
 * romaniser cannot find Thai syllable boundaries reliably: ราชดำริ comes out
 * "Rachadamri" mechanically, but every map, street sign and taxi driver in
 * Bangkok says "Ratchadamri". Where a conventional spelling exists it IS the
 * correct answer and the mechanical one is wrong.
 *
 * This list is not exhaustive and is not meant to be - it covers the major
 * named roads a driver actually navigates by, and anything missing still gets
 * a readable (if approximate) romanisation from the rules below. Add to it
 * when a name comes out wrong on a real route.
 */
const WORDS: Record<string, string> = {
  // Accommodation. These are the words hotel names are built from, and a
  // mechanical romanisation mangles the ones that are English loanwords to
  // begin with: รีสอร์ท is "Resort" spelled in Thai, and coming out as "Risot"
  // helps nobody. Longest-first matching (below) keeps โรงแรม from being split.
  โรงแรม: 'Hotel',
  รีสอร์ท: 'Resort',
  รีสอร์ต: 'Resort',
  เกสต์เฮ้าส์: 'Guesthouse',
  เกสท์เฮ้าส์: 'Guesthouse',
  โฮมสเตย์: 'Homestay',
  โฮเทล: 'Hotel',
  อพาร์ทเม้นท์: 'Apartment',
  อพาร์ตเมนต์: 'Apartment',
  แมนชั่น: 'Mansion',
  เรสซิเดนซ์: 'Residence',
  บังกะโล: 'Bungalow',
  พูลวิลล่า: 'Pool Villa',
  วิลล่า: 'Villa',
  ที่พัก: 'Accommodation',
  บ้านพัก: 'Guesthouse',
  หอพัก: 'Dormitory',
  ห้องพัก: 'Rooms',
  เขาใหญ่: 'Khao Yai',
  ปากช่อง: 'Pak Chong',
  // Generic prefixes - these carry most of the value, since Thai writes road
  // names without spaces and nearly all of them start with one of these.
  ถนน: 'Thanon',
  ซอย: 'Soi',
  แยก: 'Yaek',
  สะพาน: 'Saphan',
  คลอง: 'Khlong',
  ทางหลวง: 'Thang Luang',
  ทางพิเศษ: 'Expressway',
  ทางด่วน: 'Expressway',
  วงเวียน: 'Wongwian',
  กรุงเทพมหานคร: 'Bangkok',
  กรุงเทพ: 'Bangkok',

  // Major Bangkok roads, conventional spellings.
  ราชดำริ: 'Ratchadamri',
  ราชดำเนิน: 'Ratchadamnoen',
  ราชดำเนินนอก: 'Ratchadamnoen Nok',
  ราชดำเนินกลาง: 'Ratchadamnoen Klang',
  ราชดำเนินใน: 'Ratchadamnoen Nai',
  เพชรบุรี: 'Phetchaburi',
  พระรามที่: 'Rama',
  พระราม: 'Rama',
  สุขุมวิท: 'Sukhumvit',
  สีลม: 'Silom',
  สาทร: 'Sathon',
  พหลโยธิน: 'Phahonyothin',
  วิภาวดีรังสิต: 'Vibhavadi Rangsit',
  วิภาวดี: 'Vibhavadi',
  ลาดพร้าว: 'Lat Phrao',
  รัชดาภิเษก: 'Ratchadaphisek',
  อโศกมนตรี: 'Asok Montri',
  เยาวราช: 'Yaowarat',
  จรัญสนิทวงศ์: 'Charan Sanitwong',
  บรมราชชนนี: 'Borommaratchachonnani',
  งามวงศ์วาน: 'Ngamwongwan',
  แจ้งวัฒนะ: 'Chaeng Watthana',
  รามคำแหง: 'Ramkhamhaeng',
  เพชรเกษม: 'Phetkasem',
  สาธุประดิษฐ์: 'Sathu Pradit',
  นครสวรรค์: 'Nakhon Sawan',
  ศรีอยุธยา: 'Si Ayutthaya',
  พิษณุโลก: 'Phitsanulok',
  วิสุทธิกษัตริย์: 'Wisut Kasat',
  จักรพรรดิพงษ์: 'Chakkraphatdi Phong',
  หลานหลวง: 'Lan Luang',
  ดินสอ: 'Dinso',
  ตะนาว: 'Tanao',
  มหาไชย: 'Maha Chai',
  เจริญกรุง: 'Charoen Krung',
  สุรวงศ์: 'Surawong',
  พญาไท: 'Phaya Thai',
  ราชปรารภ: 'Ratchaprarop',
  ราชวิถี: 'Ratchawithi',
  สวรรคโลก: 'Sawankhalok',
  กำแพงเพชร: 'Kamphaeng Phet',
  เทอดดำริ: 'Thoet Damri',
  ประดิพัทธ์: 'Pradiphat',
  สามเสน: 'Samsen',
  นครไชยศรี: 'Nakhon Chai Si',
  ทหาร: 'Thahan',
  ระนอง: 'Ranong',
  เตชะวณิช: 'Techawanit',
  ประชาราษฎร์: 'Pracharat',
  กรุงธนบุรี: 'Krung Thonburi',
  สมเด็จพระเจ้าตากสิน: 'Somdet Phra Chao Taksin',
  ราชพฤกษ์: 'Ratchaphruek',
  กาญจนาภิเษก: 'Kanchanaphisek',
  บางนา: 'Bang Na',
  ตราด: 'Trat',
  ศรีนครินทร์: 'Srinagarindra',
  อ่อนนุช: 'On Nut',
  พัฒนาการ: 'Phatthanakan',
  เอกมัย: 'Ekkamai',
  ทองหล่อ: 'Thonglor',
  สุขุมวิทสาย: 'Sukhumvit',
  นราธิวาสราชนครินทร์: 'Narathiwat Ratchanakharin',
  พระรามเก้า: 'Rama IX',
  ดินแดง: 'Din Daeng',
  อโศก: 'Asok',
  ชิดลม: 'Chit Lom',
  ปทุมวัน: 'Pathum Wan',
  สยาม: 'Siam',
  สยามสมาคม: 'Siam Society',
};

/**
 * Consonants, as their INITIAL sound. Thai consonants take a different sound
 * in final position; FINALS below covers that.
 */
const INITIAL: Record<string, string> = {
  ก: 'k', ข: 'kh', ฃ: 'kh', ค: 'kh', ฅ: 'kh', ฆ: 'kh',
  ง: 'ng',
  จ: 'ch', ฉ: 'ch', ช: 'ch', ซ: 's', ฌ: 'ch',
  ญ: 'y', ฎ: 'd', ฏ: 't',
  ฐ: 'th', ฑ: 'th', ฒ: 'th', ณ: 'n',
  ด: 'd', ต: 't', ถ: 'th', ท: 'th', ธ: 'th', น: 'n',
  บ: 'b', ป: 'p', ผ: 'ph', ฝ: 'f', พ: 'ph', ฟ: 'f', ภ: 'ph', ม: 'm',
  ย: 'y', ร: 'r', ล: 'l', ว: 'w',
  ศ: 's', ษ: 's', ส: 's', ห: 'h', ฬ: 'l', อ: '', ฮ: 'h',
};

/** Consonants in FINAL position - Thai neutralises finals to k/t/p/n/ng/m/i/o. */
const FINAL: Record<string, string> = {
  ก: 'k', ข: 'k', ค: 'k', ฆ: 'k',
  ง: 'ng',
  จ: 't', ช: 't', ซ: 't', ฌ: 't',
  ญ: 'n', ฎ: 't', ฏ: 't', ฐ: 't', ฑ: 't', ฒ: 't', ณ: 'n',
  ด: 't', ต: 't', ถ: 't', ท: 't', ธ: 't', น: 'n',
  บ: 'p', ป: 'p', พ: 'p', ฟ: 'p', ภ: 'p', ม: 'm',
  ย: 'i', ร: 'n', ล: 'n', ว: 'o',
  ศ: 't', ษ: 't', ส: 't', ฬ: 'n',
};

/**
 * Vowel patterns, longest first so multi-glyph vowels win over their parts.
 * `C` marks where the initial consonant goes.
 */
const VOWELS: Array<[RegExp, string]> = [
  // Leading-vowel forms: the glyph is written BEFORE the consonant it follows.
  [/^เ(.)ีย/, 'ia'],
  [/^เ(.)ือ/, 'uea'],
  [/^เ(.)อะ?/, 'oe'],
  [/^แ(.)ะ/, 'ae'],
  [/^แ(.)/, 'ae'],
  [/^โ(.)ะ/, 'o'],
  [/^โ(.)/, 'o'],
  [/^ไ(.)/, 'ai'],
  [/^ใ(.)/, 'ai'],
  [/^เ(.)า/, 'ao'],
  [/^เ(.)ะ/, 'e'],
  [/^เ(.)/, 'e'],
  // Trailing / above / below forms.
  [/^(.)ัว/, 'ua'],
  [/^(.)ือ/, 'ue'],
  [/^(.)ิว/, 'io'],
  [/^(.)ำ/, 'am'],
  [/^(.)ะ/, 'a'],
  [/^(.)ั/, 'a'],
  [/^(.)า/, 'a'],
  [/^(.)ิ/, 'i'],
  [/^(.)ี/, 'i'],
  [/^(.)ึ/, 'ue'],
  [/^(.)ื/, 'ue'],
  [/^(.)ุ/, 'u'],
  [/^(.)ู/, 'u'],
  [/^(.)อ/, 'o'],
];

/**
 * Longest first, so ราชดำเนินนอก wins over ราชดำเนิน and สุขุมวิทสาย over
 * สุขุมวิท - matching the shorter prefix first would strand the remainder.
 */
const WORDS_BY_LENGTH = Object.entries(WORDS).sort((a, b) => b[0].length - a[0].length);

/**
 * True consonant clusters: these two consonants are pronounced together as a
 * single onset, so no inherent vowel is inserted between them. Without this
 * พระราม comes out "Phararam" instead of "Phraram", and ตรี as "tari".
 */
const CLUSTERS = new Set([
  'กร', 'กล', 'กว',
  'ขร', 'ขล', 'ขว',
  'คร', 'คล', 'คว',
  'ตร',
  'ปร', 'ปล',
  'ผล',
  'พร', 'พล',
  'ทร',
  'บร', 'บล',
  'ดร',
  'ฟร', 'ฟล',
  'สร',
]);

/** Tone marks, the silencer, and vowel-length marks carry no romanised sound. */
const SILENT = /[็-๎ฺ]/g;
const THAI_CHAR = /[฀-๿]/;
const THAI_DIGITS = '๐๑๒๓๔๕๖๗๘๙';

/** Thai numerals to Arabic - "๑" and "1" name the same road. */
function normaliseDigits(s: string): string {
  return s.replace(/[๐-๙]/g, (d) => String(THAI_DIGITS.indexOf(d)));
}

/** Romanise one Thai word. */
function word(src: string): string {
  const conventional = WORDS[src];
  if (conventional) return conventional;

  /*
   * Thai is written without spaces, so "ถนนราชดำริ" is one token carrying a
   * known prefix + the actual name. Split the prefix off and romanise the
   * rest separately, or it runs together as "Thnnrachdamri" instead of
   * "Thanon Ratchadamri".
   */
  for (const [thai, latin] of WORDS_BY_LENGTH) {
    if (src.length > thai.length && src.startsWith(thai)) {
      return `${latin} ${word(src.slice(thai.length))}`.trim();
    }
  }

  /*
   * Known words also appear at the END or the MIDDLE of a compound, not just
   * as a prefix. Thai writes without spaces, so "วังน้อยรีสอร์ท" is one token
   * ending in รีสอร์ท (resort) - prefix-only matching produced
   * "Wanganoyarisot", which helps nobody. Road names rarely need this (their
   * markers lead), which is why it was not here before; hotel names need it
   * constantly, since the type word almost always trails the name.
   */
  for (const [thai, latin] of WORDS_BY_LENGTH) {
    // Only worth splitting on a substantial word - a two-character match
    // inside a name is far more likely to be a coincidence than a word.
    if (thai.length < 3) continue;
    const at = src.indexOf(thai);
    if (at > 0) {
      const head = word(src.slice(0, at));
      const tail = word(src.slice(at + thai.length));
      return `${head} ${latin} ${tail}`.trim().replace(/\s+/g, ' ');
    }
  }

  // Strip the silencer AND the consonant it kills: "ทร์" -> "" at the end of
  // ราชดำเนิน-style names, which is why they do not end in a stray "r".
  let s = src.replace(/.์/g, '').replace(SILENT, '');

  let out = '';
  while (s.length > 0) {
    const ch = s[0];
    if (!THAI_CHAR.test(ch)) {
      out += ch;
      s = s.slice(1);
      continue;
    }

    let matched = false;
    for (const [re, sound] of VOWELS) {
      const m = re.exec(s);
      if (!m) continue;
      const cons = m[1];
      // A leading ห before another consonant is a silent tone marker.
      const initial = INITIAL[cons] ?? '';
      out += initial + sound;
      s = s.slice(m[0].length);
      matched = true;
      break;
    }
    if (matched) continue;

    /*
     * A bare consonant with no written vowel. Thai has an INHERENT vowel here
     * - unwritten but pronounced - which is why ราชดำริ is "Ratchadamri" and
     * not "rachdamri". Without this the output is an unreadable consonant run.
     *
     * The rule applied: a consonant closing the word is a final (no vowel);
     * one followed by another bare consonant takes the inherent "a"; and a
     * silent ห before another consonant is a tone marker, not a sound.
     */
    const isLast = s.length === 1;
    if (isLast) {
      out += FINAL[ch] ?? '';
      s = s.slice(1);
      continue;
    }
    if (ch === 'ห' && THAI_CHAR.test(s[1] ?? '')) {
      s = s.slice(1); // leading ห: silent, only marks tone
      continue;
    }

    const next = s[1] ?? '';
    // A true cluster (พร, ตร, กล...) is one onset: emit both, no vowel between.
    if (CLUSTERS.has(ch + next)) {
      out += (INITIAL[ch] ?? '') + (INITIAL[next] ?? '');
      s = s.slice(2);
      continue;
    }
    // "Bare consonant + bare consonant" is where the inherent vowel appears.
    // If the NEXT char carries its own vowel glyph, this consonant is just an
    // onset and the vowel rules above will handle the pair.
    const nextIsBareConsonant = INITIAL[next] !== undefined;
    const nextClosesWord = s.length === 2;
    out += INITIAL[ch] ?? '';
    if (nextIsBareConsonant && !nextClosesWord) out += 'a';
    s = s.slice(1);
  }

  if (!out) return '';
  return out.charAt(0).toUpperCase() + out.slice(1);
}

/**
 * Romanise a Thai road name. Latin and numeric parts pass through untouched,
 * so "ถนนพระรามที่ 1" keeps its "1".
 *
 * Returns '' when there is nothing to romanise.
 */
export function romaniseThai(text: string): string {
  const src = normaliseDigits(text ?? '').trim();
  if (!src) return '';
  if (!THAI_CHAR.test(src)) return src;

  const romanised = src
    .split(/([\s;,/·]+)/)
    .map((part) => (THAI_CHAR.test(part) ? word(part) : part))
    .join('')
    .replace(/\s{2,}/g, ' ')
    .trim();

  /*
   * OSM often carries the same road under several names at once - Bangkok's
   * Rama I is "ถนนพระรามที่ 1; ถนนพระรามที่ ๑", the identical name written
   * with Arabic and then Thai numerals. Both romanise to "Thanon Rama 1", so
   * after normalising digits the alternatives collapse into duplicates. Show
   * each distinct name once; "Thanon Rama 1; Thanon Rama 1" is just noise on
   * a sign that has to be read at a glance.
   */
  const seen = new Set<string>();
  const parts = romanised
    .split(/\s*;\s*/)
    .map((p) => p.trim())
    .filter((p) => {
      if (!p) return false;
      const key = p.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  return parts.join('; ');
}
